import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useDirection } from '../../../contexts/DirectionContext';
import { SearchBar, LanguageSwitcher, BrandLogo, ThemeToggle } from '../../common';
import LiveBanner from '../../common/LiveBanner';
import { ROUTES } from '../../../router/routes';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isRTL } = useDirection();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const handleClickOutside = (e: React.MouseEvent) => {
    // Check if the click target is not part of the dropdown or settings button
    const dropdown = document.getElementById('settings-dropdown');
    const button = document.getElementById('settings-button');
    if (dropdown && !dropdown.contains(e.target as Node) && 
        button && !button.contains(e.target as Node)) {
      setIsSettingsOpen(false);
    }
  };

  const handleMobileSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (mobileSearchQuery.trim()) {
      const searchParams = new URLSearchParams();
      searchParams.set("term", mobileSearchQuery.trim());
      searchParams.set("contentType", "video"); // Default to video content type
      navigate(`/search?${searchParams.toString()}`);
      setIsSearchOpen(false);
      setMobileSearchQuery('');
    }
  };

  const navLinks = [
    { path: '/search', label: t('navigation.content') },
    { path: '/topics', label: t('navigation.topics') },
    { path: '/magazine', label: t('navigation.magazine') },
    { path: '/ask', label: t('navigation.askRabbi') },
    { path: '/donate', label: t('navigation.donate') },
    { path: '/live', label: t('navigation.live') },
  ];

  const isActive = (path: string) => location.pathname === path;

  const SettingsDropdown = () => (
    <div className="relative">
      <button
        onClick={() => setIsSettingsOpen(!isSettingsOpen)}
        className="p-2 text-text-primary hover:text-primary-500 transition-colors"
        aria-label={t('navigation.settings')}
      >
        <Icon icon="ph:gear-six-thin" className="w-6 h-6" />
      </button>
      {isSettingsOpen && (
        <div className={`
          absolute top-full mt-2
          ${isRTL ? 'left-0' : 'right-0'}
          w-auto
          bg-background-primary
          border border-border-primary
          ${isRTL ? 'rounded-r-md' : 'rounded-l-md'}
          shadow-lg
          z-50
          transition-colors duration-200
        `}>
          <div className="px-4 py-2 flex flex-row items-center gap-4">
            <div className="flex-shrink-0">
              <LanguageSwitcher menuStyle={true} />
            </div>
            <div className="h-8 w-px bg-border-primary" />
            <div className="flex-shrink-0">
              <ThemeToggle />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile Menu Drawer */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-[1200] flex">
          {/* Menu Content */}
          <div className={`
            w-full max-w-[320px] ${isRTL ? 'right-0' : 'left-0'}
            flex flex-col h-full
            bg-background-primary
            border-r border-border-primary dark:border-border-secondary
          `}>
            {/* Header */}
            <div className="flex items-center h-16 px-4 flex-shrink-0">
              <BrandLogo 
                imageClassName="h-9 w-9 rounded-full object-cover"
                showLink={false}
                className="flex items-center gap-3"
              />
              <button
                onClick={() => setIsMenuOpen(false)}
                className={`
                  p-2 ${isRTL ? 'mr-auto' : 'ml-auto'}
                  text-text-primary hover:text-primary-500
                  transition-colors
                `}
                aria-label={t('common.close')}
              >
                <Icon icon="ph:x-thin" className="w-6 h-6" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto flex flex-col">
              <div className="flex flex-col space-y-4 px-8 py-8">
                {/* Primary Navigation Links */}
                <div className="flex flex-col space-y-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={`
                        text-base
                        ${isActive(link.path)
                          ? 'text-primary-500 font-bold'
                          : 'text-text-primary hover:text-primary-500'}
                        transition-colors duration-200
                      `}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>

                {/* Divider */}
                <div className="w-full h-px bg-border-primary" />

                {/* Secondary Links */}
                <div className="flex flex-col space-y-4">
                  <Link
                    to={ROUTES.MAGAZINE}
                    onClick={() => setIsMenuOpen(false)}
                    className="text-base text-primary-500 hover:text-primary-600 transition-colors"
                  >
                    {t('navigation.login')}
                  </Link>
                  <Link
                    to={ROUTES.MAGAZINE}
                    onClick={() => setIsMenuOpen(false)}
                    className="text-base text-primary-500 hover:text-primary-600 transition-colors"
                  >
                    {t('navigation.register')}
                  </Link>
                </div>
              </div>
              
              {/* Spacer to push dedication to bottom */}
              <div className="flex-grow"></div>
              
              {/* Dedication */}
              <div className="px-8 pb-4">
                <div className="flex flex-col space-y-2">
                  <p className="text-sm text-text-secondary italic font-light">
                    {t('dedication.translation')}
                  </p>
                </div>
              </div>
            </div>

            {/* Language Selector and Theme Toggle */}
            <div className="flex-shrink-0 px-8 py-4 border-t border-border-primary">
              <div className="flex items-center gap-4">
                <ThemeToggle />
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <LanguageSwitcher openUpward={true} menuStyle={true} />
                </div>
              </div>
            </div>
          </div>

          {/* Overlay */}
          <div 
            className="flex-1 bg-black bg-opacity-80 backdrop-blur-sm" 
            onClick={() => setIsMenuOpen(false)}
          />
        </div>
      )}

      {/* Navbar */}
      <nav className="
        fixed top-0 left-0 right-0 z-[1100]
        bg-background-primary
        border-b border-border-primary
        transition-colors duration-200
      ">
        <LiveBanner />
        {/* Mobile Navigation */}
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-4" style={{ height: 'var(--header-height)' }}>
            {isSearchOpen ? (
              <form onSubmit={handleMobileSearch} className="flex items-center w-full gap-2">
                <input
                  type="text"
                  value={mobileSearchQuery}
                  onChange={(e) => setMobileSearchQuery(e.target.value)}
                  placeholder={t('common.search')}
                  className="
                    flex-1 bg-transparent
                    text-text-primary placeholder-text-secondary
                    focus:outline-none
                    text-base
                  "
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setIsSearchOpen(false)}
                  className="p-2 text-text-primary hover:text-primary-500 transition-colors"
                  aria-label={t('common.close')}
                >
                  <Icon icon="ph:x-thin" className="w-6 h-6" />
                </button>
              </form>
            ) : (
              <>
                <button
                  className="p-2 text-text-primary hover:text-primary-500 transition-colors"
                  onClick={() => setIsSearchOpen(true)}
                  aria-label={t('common.search')}
                >
                  <Icon icon="ph:magnifying-glass-thin" className="w-6 h-6" />
                </button>

                <BrandLogo 
                  imageClassName="h-8 w-auto"
                  textClassName="text-2xl font-bold text-text-primary font-major"
                  className="flex items-center gap-3"
                  showLink={true}
                />

                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="p-2 text-text-primary hover:text-primary-500 transition-colors"
                  aria-expanded={isMenuOpen}
                  aria-label={isMenuOpen ? t('common.close') : t('common.menu')}
                >
                  <Icon icon="ph:list-thin" className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center" style={{ height: 'var(--header-height)' }}>
            {/* Logo and Primary Nav */}
            <div className="flex items-center flex-shrink-0">
              <div className="flex-shrink-0">
                <BrandLogo 
                  textClassName="text-2xl font-bold text-text-primary font-major"
                  className="flex items-center gap-3"
                  withMargin={true}
                />
              </div>
              <div className="hidden lg:flex lg:items-center">
                <div className={`flex items-center space-x-1 rtl:space-x-reverse ${isRTL ? 'mr-4' : 'ml-4'}`}>
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`
                        inline-flex items-center px-3 pt-1
                        text-sm font-medium whitespace-nowrap
                        transition-colors duration-200
                        ${isActive(link.path)
                          ? 'text-primary-500 font-bold'
                          : 'text-text-primary hover:text-primary-500'}
                      `}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Search, Theme, and Language */}
            <div className="flex items-center gap-6 flex-shrink">
              <div className="w-[256px] min-w-[120px] flex-shrink transition-all duration-200">
                <SearchBar />
              </div>
              {/* Full-size theme and language toggles */}
              <div className="hidden xl:flex items-center gap-4 flex-shrink-0">
                <LanguageSwitcher />
                <ThemeToggle />
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Floating Settings Button for intermediate screens */}
      <div className={`
        fixed z-40 hidden lg:flex xl:hidden
        ${isRTL ? 'left-0' : 'right-0'}
        top-[calc(var(--header-total-height)_+_1rem)]
        bg-background-primary
        border border-border-primary
        ${isRTL ? 'rounded-r-md' : 'rounded-l-md'}
        shadow-lg
        transition-all duration-200
      `}>
        <SettingsDropdown />
      </div>

      {/* Click outside handler for settings dropdown */}
      {isSettingsOpen && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setIsSettingsOpen(false)}
        />
      )}
    </>
  );
};

export default Navbar; 